/**
 * Config resolution with prototype pollution prevention.
 * Merges user options into defaults and freezes the result.
 */

import type { CloudConfig, CloudReporterOptions } from '@testrelic/core';
import { createError, ErrorCode } from '@testrelic/core';
import type { AppiumReporterConfig, ResolvedAppiumConfig } from './types.js';
import { discoverConfigFile, parseConfigFile, resolveEnvVars, mergeCloudConfig } from './cloud-config.js';

const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

export const DEFAULT_REDACTION_PATTERNS: (string | RegExp)[] = [
  /AKIA[A-Z0-9]{16}/g,
  /Bearer\s+[A-Za-z0-9\-._~+/]+=*/g,
  /-----BEGIN\s+(RSA\s+)?PRIVATE\sKEY-----[\s\S]*?-----END/g,
  /\/\/[^:]+:[^@]+@/g,
];

function hasPrototypePollution(obj: unknown): boolean {
  if (typeof obj !== 'object' || obj === null) return false;
  for (const key of Object.keys(obj)) {
    if (DANGEROUS_KEYS.has(key)) return true;
  }
  return false;
}

function isValidAppiumConfig(input: unknown): input is AppiumReporterConfig {
  if (typeof input !== 'object' || input === null) return false;
  if (hasPrototypePollution(input)) return false;

  const obj = input as Record<string, unknown>;
  if (obj.outputPath !== undefined && typeof obj.outputPath !== 'string') return false;
  if (obj.htmlReportPath !== undefined && typeof obj.htmlReportPath !== 'string') return false;
  if (obj.openReport !== undefined && typeof obj.openReport !== 'boolean') return false;
  if (obj.includeDeviceLogs !== undefined && typeof obj.includeDeviceLogs !== 'boolean') return false;
  if (obj.includeNetworkLogs !== undefined && typeof obj.includeNetworkLogs !== 'boolean') return false;
  if (obj.includeConsoleLogs !== undefined && typeof obj.includeConsoleLogs !== 'boolean') return false;
  if (obj.includeScreenshots !== undefined && typeof obj.includeScreenshots !== 'boolean') return false;
  if (obj.includeVideoRecording !== undefined && typeof obj.includeVideoRecording !== 'boolean') return false;
  if (obj.includeCommands !== undefined && typeof obj.includeCommands !== 'boolean') return false;
  if (obj.includeAssertions !== undefined && typeof obj.includeAssertions !== 'boolean') return false;
  if (obj.includeActionSteps !== undefined && typeof obj.includeActionSteps !== 'boolean') return false;
  if (obj.preferBiDi !== undefined && typeof obj.preferBiDi !== 'boolean') return false;
  if (obj.quiet !== undefined && typeof obj.quiet !== 'boolean') return false;

  if (obj.deviceLogPollInterval !== undefined) {
    if (typeof obj.deviceLogPollInterval !== 'number' || obj.deviceLogPollInterval < 100) return false;
  }

  if (obj.maxDeviceLogs !== undefined) {
    if (typeof obj.maxDeviceLogs !== 'number' || !Number.isInteger(obj.maxDeviceLogs) || obj.maxDeviceLogs < 0) return false;
  }

  if (obj.screenshotOnEvery !== undefined) {
    if (!['test', 'failure', 'never'].includes(obj.screenshotOnEvery as string)) return false;
  }

  if (obj.redactPatterns !== undefined) {
    if (!Array.isArray(obj.redactPatterns)) return false;
    for (const p of obj.redactPatterns) {
      if (typeof p !== 'string' && !(p instanceof RegExp)) return false;
    }
  }

  if (obj.metadata !== undefined && obj.metadata !== null) {
    if (typeof obj.metadata !== 'object') return false;
    if (hasPrototypePollution(obj.metadata)) return false;
  }

  return true;
}

export function resolveConfig(options?: Partial<AppiumReporterConfig>): ResolvedAppiumConfig {
  if (options !== undefined && !isValidAppiumConfig(options)) {
    throw createError(ErrorCode.CONFIG_INVALID, 'Invalid Appium reporter configuration');
  }

  const target = Object.create(null) as Record<string, unknown>;
  const outputPath = options?.outputPath ?? './test-results/testrelic-timeline.json';

  target.outputPath = outputPath;
  target.htmlReportPath = options?.htmlReportPath ?? outputPath.replace(/\.json$/, '.html');
  target.openReport = options?.openReport ?? true;
  target.includeDeviceLogs = options?.includeDeviceLogs ?? true;
  target.includeNetworkLogs = options?.includeNetworkLogs ?? true;
  target.includeConsoleLogs = options?.includeConsoleLogs ?? true;
  target.includeScreenshots = options?.includeScreenshots ?? true;
  target.screenshotOnEvery = options?.screenshotOnEvery ?? 'failure';
  target.includeVideoRecording = options?.includeVideoRecording ?? false;
  target.includeCommands = options?.includeCommands ?? true;
  target.includeAssertions = options?.includeAssertions ?? true;
  target.includeActionSteps = options?.includeActionSteps ?? true;
  target.deviceLogPollInterval = options?.deviceLogPollInterval ?? 1000;
  target.maxDeviceLogs = options?.maxDeviceLogs ?? 2000;
  target.preferBiDi = options?.preferBiDi ?? true;
  target.redactPatterns = [
    ...DEFAULT_REDACTION_PATTERNS,
    ...(options?.redactPatterns ?? []),
  ];
  target.testRunId = options?.testRunId ?? null;
  target.metadata = options?.metadata ?? null;
  target.quiet = options?.quiet ?? false;
  target.reportMode = options?.reportMode ?? 'streaming';
  target.streamingThreshold = options?.streamingThreshold ?? 0;

  target.cloud = resolveCloudFromMerge(options?.cloud ?? null);

  return Object.freeze(target) as unknown as ResolvedAppiumConfig;
}

function resolveCloudFromMerge(
  reporterOptions: CloudReporterOptions | null | undefined,
): CloudConfig | null {
  const configPath = discoverConfigFile(process.cwd());
  const fileConfig = configPath ? parseConfigFile(configPath) : null;
  const resolvedFile = fileConfig ? resolveEnvVars(fileConfig) : null;
  const merged = mergeCloudConfig(
    resolvedFile as Record<string, unknown> | null,
    reporterOptions ?? undefined,
  );
  return merged.apiKey ? merged : null;
}
